/**
 * Lê o `.eml` cru (RFC 5322 + MIME) o bastante para a tela mostrar o e-mail como um leitor de e-mail mostraria:
 * cabeçalhos decodificados, o corpo (HTML de preferência, texto se não houver) e as imagens embutidas por `cid:`.
 *
 * Sem biblioteca nova: a tela só precisa do caminho comum (multipart, base64, quoted-printable, RFC 2047). O que
 * não for entendido aqui continua no download do `.eml`.
 */

export interface EmailLido {
  readonly de: string | null;
  readonly para: string | null;
  readonly data: string | null;
  readonly assunto: string | null;
  readonly html: string | null;
  readonly texto: string | null;
  /** `Content-ID` sem `< >` → `data:` URL da imagem. */
  readonly imagensPorCid: ReadonlyMap<string, string>;
}

interface Parte {
  readonly cabecalhos: ReadonlyMap<string, string>;
  readonly corpo: string;
}

interface ValorComParametros {
  readonly valor: string;
  readonly parametros: ReadonlyMap<string, string>;
}

/** Tipos que o navegador não reconhece em `data:` pelo nome antigo. */
const TIPOS_DE_IMAGEM_EQUIVALENTES: Readonly<Record<string, string>> = {
  'image/x-png': 'image/png',
  'image/pjpeg': 'image/jpeg',
  'image/jpg': 'image/jpeg',
};

function decodificarBytes(bytes: Uint8Array, charset: string | undefined): string {
  try {
    return new TextDecoder(charset ?? 'utf-8').decode(bytes);
  } catch {
    // Charset que o navegador não conhece: UTF-8 erra acento, mas não perde o resto do texto.
    return new TextDecoder('utf-8').decode(bytes);
  }
}

/**
 * O `.eml` chega como texto já decodificado em UTF-8, então um caractere pode valer mais de um byte. Os trechos
 * em base64 e quoted-printable são ASCII; o resto volta aos bytes UTF-8 para não estragar o que já estava certo.
 */
function bytesDoTexto(texto: string): Uint8Array {
  const bytes: number[] = [];
  for (const caractere of texto) {
    const codigo = caractere.codePointAt(0) ?? 0;
    if (codigo < 0x80) {
      bytes.push(codigo);
    } else {
      bytes.push(...new TextEncoder().encode(caractere));
    }
  }
  return Uint8Array.from(bytes);
}

function bytesDoBase64(base64: string): Uint8Array {
  try {
    const binario = atob(base64.replace(/[^A-Za-z0-9+/=]/g, ''));
    return Uint8Array.from(binario, (c) => c.charCodeAt(0));
  } catch {
    return new Uint8Array();
  }
}

function bytesDoQuotedPrintable(texto: string, emCabecalho: boolean): Uint8Array {
  const semQuebraSuave = emCabecalho ? texto.replace(/_/g, ' ') : texto.replace(/=\r?\n/g, '');
  const bytesCrus = bytesDoTexto(semQuebraSuave);
  const saida: number[] = [];
  const ehHex = (b: number | undefined): boolean => b !== undefined && /[0-9A-Fa-f]/.test(String.fromCharCode(b));
  for (let i = 0; i < bytesCrus.length; i++) {
    const byte = bytesCrus[i];
    if (byte === undefined) continue;
    if (byte === 0x3d && ehHex(bytesCrus[i + 1]) && ehHex(bytesCrus[i + 2])) {
      saida.push(parseInt(String.fromCharCode(bytesCrus[i + 1] ?? 0, bytesCrus[i + 2] ?? 0), 16));
      i += 2;
    } else {
      saida.push(byte);
    }
  }
  return Uint8Array.from(saida);
}

/** `=?UTF-8?Q?An=C3=A1lise?=` → `Análise` (RFC 2047). Palavras codificadas vizinhas se juntam sem o espaço. */
export function decodificarCabecalho(valor: string): string {
  return valor
    .replace(/(=\?[^?]+\?[bBqQ]\?[^?]*\?=)\s+(?==\?[^?]+\?[bBqQ]\?[^?]*\?=)/g, '$1')
    .replace(/=\?([^?]+)\?([bBqQ])\?([^?]*)\?=/g, (_trecho, charset: string, codificacao: string, texto: string) => {
      const bytes = codificacao.toUpperCase() === 'B' ? bytesDoBase64(texto) : bytesDoQuotedPrintable(texto, true);
      return decodificarBytes(bytes, charset);
    });
}

function separarCabecalhosECorpo(bruto: string): Parte {
  const fim = /\r?\n\r?\n/.exec(bruto);
  const cabecalhoBruto = fim === null ? bruto : bruto.slice(0, fim.index);
  const corpo = fim === null ? '' : bruto.slice(fim.index + fim[0].length);

  const cabecalhos = new Map<string, string>();
  let atual: string | null = null;
  for (const linha of cabecalhoBruto.split(/\r?\n/)) {
    if (/^[ \t]/.test(linha) && atual !== null) {
      cabecalhos.set(atual, `${cabecalhos.get(atual) ?? ''} ${linha.trim()}`);
      continue;
    }
    const doisPontos = linha.indexOf(':');
    if (doisPontos <= 0) {
      atual = null;
      continue;
    }
    atual = linha.slice(0, doisPontos).trim().toLowerCase();
    // `Received` e afins se repetem; o que a tela usa aparece uma vez, e vale o primeiro.
    if (!cabecalhos.has(atual)) cabecalhos.set(atual, linha.slice(doisPontos + 1).trim());
    else atual = null;
  }
  return { cabecalhos, corpo };
}

function lerValorComParametros(valor: string | undefined): ValorComParametros {
  const [principal = '', ...resto] = (valor ?? '').split(';');
  const parametros = new Map<string, string>();
  for (const pedaco of resto) {
    const igual = pedaco.indexOf('=');
    if (igual <= 0) continue;
    const nome = pedaco.slice(0, igual).trim().toLowerCase();
    const bruto = pedaco.slice(igual + 1).trim();
    parametros.set(nome, bruto.replace(/^"(.*)"$/s, '$1'));
  }
  return { valor: principal.trim().toLowerCase(), parametros };
}

/** Um e-mail sem cabeçalho nenhum (o mock, um arquivo cortado) vira texto puro, em vez de sumir. */
function pareceTerCabecalho(bruto: string): boolean {
  return /^[A-Za-z][A-Za-z0-9-]*:/.test(bruto.trimStart());
}

function dividirMultipart(corpo: string, fronteira: string): string[] {
  const delimitador = `--${fronteira}`;
  const partes: string[] = [];
  const pedacos = corpo.split(delimitador);
  // O que vem antes do primeiro delimitador é preâmbulo; o pedaço que começa com `--` é o fechamento.
  for (const pedaco of pedacos.slice(1)) {
    if (pedaco.startsWith('--')) break;
    partes.push(pedaco.replace(/^[ \t]*\r?\n/, '').replace(/\r?\n$/, ''));
  }
  return partes;
}

interface Coleta {
  html: string | null;
  texto: string | null;
  readonly imagensPorCid: Map<string, string>;
}

function decodificarTexto(parte: Parte, charset: string | undefined): string {
  const codificacao = (parte.cabecalhos.get('content-transfer-encoding') ?? '').trim().toLowerCase();
  if (codificacao === 'base64') return decodificarBytes(bytesDoBase64(parte.corpo), charset);
  if (codificacao === 'quoted-printable') return decodificarBytes(bytesDoQuotedPrintable(parte.corpo, false), charset);
  return parte.corpo;
}

function percorrer(parte: Parte, coleta: Coleta, profundidade: number): void {
  if (profundidade > 10) return;
  const tipo = lerValorComParametros(parte.cabecalhos.get('content-type') ?? 'text/plain');
  const disposicao = lerValorComParametros(parte.cabecalhos.get('content-disposition'));
  const ehAnexo = disposicao.valor === 'attachment';

  if (tipo.valor.startsWith('multipart/')) {
    const fronteira = tipo.parametros.get('boundary');
    if (fronteira === undefined) return;
    for (const filha of dividirMultipart(parte.corpo, fronteira)) {
      percorrer(separarCabecalhosECorpo(filha), coleta, profundidade + 1);
    }
    return;
  }

  if (tipo.valor.startsWith('image/')) {
    const cid = parte.cabecalhos.get('content-id');
    const codificacao = (parte.cabecalhos.get('content-transfer-encoding') ?? '').trim().toLowerCase();
    if (cid !== undefined && codificacao === 'base64') {
      const tipoDaImagem = TIPOS_DE_IMAGEM_EQUIVALENTES[tipo.valor] ?? tipo.valor;
      coleta.imagensPorCid.set(normalizarCid(cid), `data:${tipoDaImagem};base64,${parte.corpo.replace(/\s/g, '')}`);
    }
    return;
  }

  if (ehAnexo) return;
  if (tipo.valor === 'text/html' && coleta.html === null) {
    coleta.html = decodificarTexto(parte, tipo.parametros.get('charset'));
  } else if (tipo.valor === 'text/plain' && coleta.texto === null) {
    coleta.texto = decodificarTexto(parte, tipo.parametros.get('charset'));
  }
}

/** `<imagem@x>` e `imagem@x` são o mesmo `Content-ID`; `cid:` no HTML vem sem os sinais. */
export function normalizarCid(cid: string): string {
  return cid.trim().replace(/^<|>$/g, '').toLowerCase();
}

export function lerEml(bruto: string): EmailLido {
  if (!pareceTerCabecalho(bruto)) {
    return { de: null, para: null, data: null, assunto: null, html: null, texto: bruto, imagensPorCid: new Map() };
  }

  const raiz = separarCabecalhosECorpo(bruto);
  const coleta: Coleta = { html: null, texto: null, imagensPorCid: new Map() };
  percorrer(raiz, coleta, 0);

  const cabecalho = (nome: string): string | null => {
    const valor = raiz.cabecalhos.get(nome);
    return valor === undefined || valor.trim() === '' ? null : decodificarCabecalho(valor);
  };

  return {
    de: cabecalho('from'),
    para: cabecalho('to'),
    data: cabecalho('date'),
    assunto: cabecalho('subject'),
    html: coleta.html,
    texto: coleta.texto,
    imagensPorCid: coleta.imagensPorCid,
  };
}
